/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { compose } from 'recompose';
import {
    object, shape, string, func, array, bool, arrayOf, number,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { bindActionCreators } from 'redux';

// helper
import { connect } from 'react-redux';
import subscriptionConfig, { getFirstSubscriptionOption, getSubscriptionOptions } from '../../../../../helpers/common/subscription/subscription-helpers';
import {
    capitalize,
} from './Helper';

// UI
import  { trackEvent as track } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBaseCode } from '../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { getActiveABTests } from '../../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import CollapsableSubscription from './CollapsableSubscription';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const styles = (theme) => ({
    subscriptionContainer: {
        backgroundColor: '#F9F6F0',
        borderRadius: '6px',
        border: '1px solid #F2ECE3',
        [theme.breakpoints.up('1225')]: {
            flexWrap: 'nowrap',
        },
    },
    subscriptionDiscoveryContainer: {
        border: `1px solid ${(theme.palette.grey8)}`,
        borderRadius: '4px',
        padding: '9px 0px 9px 10px',
        [theme.breakpoints.down('sm')]: {
            padding: '9px 5px',
        },
    },
    variantSubscribeBox: {
        padding: 0,
    },
    marginBottom15: {
        marginBottom: '10px',
    },
});

const Subscription = ({
    classes,
    subscriptionDetails,
    subscription,
    triggerSubscriptionCheck,
    brand,
    subscriptionsList, // redux data
    setSubscriptionDetailData,
    selectedSku,
    isSubscriptionDurationListEnabled,
    isShowingFrequencyDropdown,
    durationActions,
    frequencyActions,
    subscriptionCopyActions,
    partNumber,
    trackEvent,
    pdpOfferContent,
    globalSubscriptionCopy,
}) => {
    const {
        subscriptionDuration: productSubscriptionDuration,
        subscriptionFrequency,
    } = subscriptionDetails;
    const {
        subscriptionCheckbox,
    } = subscription;

    const { durationValue, setDurationValue } = durationActions;

    const { frequencyValue, setFrequencyValue } = frequencyActions;

    const { subscriptionCopy } = subscriptionCopyActions;

    const subscriptionDuration = getFirstSubscriptionOption(selectedSku?.subscriptionsList?.duration) || productSubscriptionDuration;

    const setHandleDataWithChangeOrDefault = (subsDuration, intervalFrequency, intervalData, typeOfSubscription) => {
        setSubscriptionDetailData({
            duration: subsDuration,
            subscriptionInterval: intervalFrequency,
            interval: intervalData,
            subscriptionType: capitalize(getFirstSubscriptionOption(selectedSku.subscriptionsList?.type) || '') || typeOfSubscription,
        });
    };

    const handleChange = (event, durationOrFrequency, subscriptionOptionList) => {
        const durationOrFrequencySelectedValue = subscriptionOptionList.filter((item) => item.id === Number(event.target.value));
        trackEvent({
            eventCategory: 'subscription',
            eventAction: durationOrFrequency,
            eventLabel: `${durationOrFrequencySelectedValue?.[0]?.value.toLowerCase?.() || ''} | ${getBaseCode(partNumber)}`  || '',
            eventValue: '1',
        });
        if (durationOrFrequency === 'duration') {
            const selectedDuration = Number(event.target.value);
            setDurationValue(selectedDuration);

            // updating redux
            setHandleDataWithChangeOrDefault(selectedDuration, frequencyValue, getSubscriptionOptions(selectedSku.subscriptionsList.interval), subscriptionsList?.subscriptionType);
        } else {
            const subscriptionInterval = Number(event.target.value);
            setFrequencyValue(subscriptionInterval);

            // updating redux
            setHandleDataWithChangeOrDefault(durationValue, subscriptionInterval, getSubscriptionOptions(selectedSku.subscriptionsList.interval), subscriptionsList?.subscriptionType);
        }
    };

    return (
        <Grid item xs={12} sm={12} md={12} className={classes.marginBottom15}>
            <Grid container className={classes.subscriptionContainer}>
                <CollapsableSubscription
                    subscriptionCopy={subscriptionCopy}
                    subscriptionDetailsFrequencyValue={subscriptionFrequency}
                    subscriptionDetailsDurationValue={subscriptionDuration}
                    subscriptionConfig={subscriptionConfig}
                    brand={brand}
                    isShowingFrequencyDropdown={isShowingFrequencyDropdown}
                    isSubscriptionDurationListEnabled={isSubscriptionDurationListEnabled}
                    selectValue={frequencyValue}
                    selectedSku={selectedSku}
                    handleChange={handleChange}
                    durationValue={durationValue}
                    subscriptionCheckbox={subscriptionCheckbox}
                    triggerSubscriptionCheck={triggerSubscriptionCheck}
                    trackEvent={trackEvent}
                    pdpOfferContent={pdpOfferContent}
                    globalSubscriptionCopy={globalSubscriptionCopy}
                />
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state) => ({
    activeABTests: getActiveABTests(state),
    isSubscriptionPdpCollapsed: getFeatureFlag('is-subscription-pdp-collapsed-enabled')(state),
});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

Subscription.propTypes = {
    classes: object.isRequired,
    subscriptionsList: shape({
        duration: arrayOf(
            number,
        ),
        type: arrayOf(
            string,
        ),
        interval: arrayOf(
            number,
        ),
    }),
    subscriptionDetails: shape({
        subscriptionType: string.isRequired,
        subscriptionDuration: string.isRequired,
        subscriptionFrequency: string.isRequired,
        subscriptionDurationKey: string.isRequired,
    }).isRequired,
    subscription: shape({
        subscriptionKey: array.isRequired,
        subscriptionIndefinite: string.isRequired,
        subscriptionType: array.isRequired,
        implementProductSubscription: bool.isRequired,
        viewSubscription: bool.isRequired,
        isTypeDiscovery: bool.isRequired,
        subscriptionCheckbox: bool.isRequired,
    }).isRequired,
    setSubscriptionDetailData: func.isRequired,
    triggerSubscriptionCheck: func.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,

    selectedSku: object.isRequired,
    isSubscriptionDurationListEnabled: bool.isRequired,
    isShowingFrequencyDropdown: bool.isRequired,
    durationActions: shape({
        durationValue: string.isRequired,
        setDurationValue: func.isRequired,
    }).isRequired,
    frequencyActions: shape({
        frequencyValue: string.isRequired,
        setFrequencyValue: func.isRequired,
    }).isRequired,
    subscriptionCopyActions: shape({
        subscriptionCopy: string.isRequired,
        setSubscriptionCopy: func.isRequired,
    }).isRequired,
    partNumber: string.isRequired,
    trackEvent: func.isRequired,
    pdpOfferContent: object.isRequired,
    globalSubscriptionCopy: object.isRequired,
};

Subscription.defaultProps = {
    subscriptionsList: {},
};
export default compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(Subscription);
